import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, ConflictError } from '../api/client';
import type { CountSheet } from '../api/types';
import { useApp } from '../state/AppContext';
import { addDays, TARGET_SOURCE, weekEndingOf } from '../state/periods';
import { ConflictBanner, ErrorBanner, SortableHeader, formatDateTime, formatLongDate, useTableSort } from '../components/ui';

const keyOf = (equipmentTypeId: number) => String(equipmentTypeId);

/**
 * One location's count for one period. Every item on the count list needs a
 * number — 0 when none are on hand — because a blank would be read as "none" by
 * anyone adding the week up, when it really means nobody counted.
 */
export function CountEntry() {
  const { locationId } = useParams();
  const [params] = useSearchParams();
  const periodEnd = params.get('periodEnd') ?? undefined;
  // Lives above the form, which remounts with fresh data after a save.
  const [savedFor, setSavedFor] = useState<string | null>(null);

  const sheet = useQuery({
    queryKey: ['count-sheet', locationId, periodEnd ?? 'current'],
    queryFn: () => api.get<CountSheet>(`/api/counts/${locationId}`, { periodEnd }),
  });

  if (sheet.isLoading) return <div className="spinner-note">Loading the count…</div>;
  if (sheet.isError || !sheet.data) {
    return <ErrorBanner message={sheet.error ? (sheet.error as Error).message : 'That count could not be loaded.'} />;
  }

  const s = sheet.data;
  const formKey = `${s.location.locationId}-${s.periodEnd}-${s.appRowVersionNo}-${s.session?.countSessionId ?? 0}`;
  return (
    <CountForm
      key={formKey}
      sheet={s}
      saved={savedFor === `${s.location.locationId}-${s.periodEnd}`}
      onSaved={() => setSavedFor(`${s.location.locationId}-${s.periodEnd}`)}
    />
  );
}

function CountForm({ sheet, saved, onSaved }: { sheet: CountSheet; saved: boolean; onSaved: () => void }) {
  const { reference, regionName } = useApp();
  const queryClient = useQueryClient();

  const initial = useMemo(() => {
    const values: Record<string, string> = {};
    for (const line of sheet.session?.lines ?? []) values[keyOf(line.equipmentTypeId)] = String(line.quantity);
    return values;
  }, [sheet]);

  const lastDay = sheet.today < sheet.periodEnd ? sheet.today : sheet.periodEnd;
  const [values, setValues] = useState(initial);
  const [note, setNote] = useState(sheet.session?.note ?? '');
  const [businessDate, setBusinessDate] = useState(sheet.session?.source === 'app' ? sheet.session.businessDate : lastDay);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);

  // The list is where counting starts, not a limit: a counter can add any other
  // supply the location has, and take off one that was added before.
  const [added, setAdded] = useState<number[]>([]);
  const [removed, setRemoved] = useState<number[]>([]);
  const rows = [
    ...sheet.items.filter((item) => item.onList || !removed.includes(item.equipmentTypeId)),
    ...added.flatMap((equipmentTypeId) => {
      const supply = reference.equipment.find((e) => e.equipmentTypeId === equipmentTypeId);
      return supply ? [{ equipmentTypeId, name: supply.name, onList: false, target: null }] : [];
    }),
  ];
  const addable = reference.equipment
    .filter((e) => e.isActive && !rows.some((r) => r.equipmentTypeId === e.equipmentTypeId));

  // On hand is left unsortable: rows would move while someone is typing into them.
  const { rows: shownRows, sort, onSort } = useTableSort(rows, {
    supply: (item) => item.name,
    minimum: (item) => item.target?.minimum,
  });

  const addSupply = (equipmentTypeId: number) => {
    if (sheet.items.some((i) => i.equipmentTypeId === equipmentTypeId)) {
      setRemoved((prev) => prev.filter((x) => x !== equipmentTypeId));
    } else {
      setAdded((prev) => [...prev, equipmentTypeId]);
    }
  };
  const removeSupply = (equipmentTypeId: number) => {
    setAdded((prev) => prev.filter((x) => x !== equipmentTypeId));
    if (sheet.items.some((i) => i.equipmentTypeId === equipmentTypeId)) setRemoved((prev) => [...prev, equipmentTypeId]);
    setValues((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== keyOf(equipmentTypeId))));
  };

  const keys = rows.map((item) => keyOf(item.equipmentTypeId));
  const filled = (k: string) => /^\d+$/.test(values[k] ?? '');
  const blanks = keys.filter((k) => !filled(k)).length;
  const readOnly = !sheet.canSave;
  const weekly = sheet.location.frequency === 'weekly';
  const step = weekly ? 7 : 1;
  const newest = weekly ? reference.currentWeekEnding : reference.today;
  const previous = addDays(sheet.periodEnd, -step);
  const next = addDays(sheet.periodEnd, step);
  const backWeek = weekEndingOf(sheet.periodEnd);
  const id = sheet.location.locationId;

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/counts/${id}`, {
        periodEnd: sheet.periodEnd,
        businessDate,
        note: note.trim() || null,
        lines: keys.map((k) => ({ equipmentTypeId: Number(k), quantity: Number(values[k]) })),
        rowVersionNo: sheet.appRowVersionNo,
      }),
    onSuccess: () => {
      onSaved();
      void queryClient.invalidateQueries({ queryKey: ['count-sheet'] });
      void queryClient.invalidateQueries({ queryKey: ['counts'] });
      void queryClient.invalidateQueries({ queryKey: ['rollup'] });
    },
    onError: (e: Error) => (e instanceof ConflictError ? setConflict(e.message) : setError(e.message)),
  });

  const setValue = (k: string, raw: string) => {
    setValues((prev) => ({ ...prev, [k]: raw.replace(/[^\d]/g, '') }));
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{sheet.location.code}</div>
          <div className="page-subtitle">
            {sheet.location.type} · {sheet.location.group} · {regionName(sheet.location.regionCode)} ·{' '}
            {weekly
              ? `week ${formatLongDate(sheet.periodStart)} – ${formatLongDate(sheet.periodEnd)}`
              : formatLongDate(sheet.periodEnd)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link className="btn btn--sm" to={`/counts/${id}?periodEnd=${previous}`}>
            ← {weekly ? 'Previous week' : 'Previous day'}
          </Link>
          {next <= newest && (
            <Link className="btn btn--sm" to={`/counts/${id}?periodEnd=${next}`}>
              {weekly ? 'Next week' : 'Next day'} →
            </Link>
          )}
          <Link className="btn btn--sm" to={backWeek === reference.currentWeekEnding ? '/counts' : `/counts?week=${backWeek}`}>
            All counts
          </Link>
        </div>
      </div>

      {saved && (
        <div className="banner banner--info">
          <div><strong>Saved.</strong> {sheet.location.code}'s count for this {weekly ? 'week' : 'day'} is recorded.</div>
        </div>
      )}
      {conflict && (
        <ConflictBanner
          message={conflict}
          onReload={() => { setConflict(null); void queryClient.invalidateQueries({ queryKey: ['count-sheet'] }); }}
        />
      )}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {readOnly && sheet.cannotSaveReason && (
        <div className="banner banner--locked"><div>{sheet.cannotSaveReason}</div></div>
      )}

      {sheet.session?.source === 'spreadsheet' && (
        <div className="banner banner--info">
          <div>
            These numbers came from the spreadsheet import (counted {formatLongDate(sheet.session.businessDate)}).
            {!readOnly && ' Check them and save to record the count as yours.'}
          </div>
        </div>
      )}
      {sheet.session?.source === 'app' && (
        <div className="meta" style={{ marginBottom: 12 }}>
          Last saved by {sheet.session.updatedBy ?? sheet.session.submittedBy} on{' '}
          {formatDateTime(sheet.session.updatedAtUtc ?? sheet.session.submittedAtUtc)}.
        </div>
      )}
      {sheet.otherSessions.length > 0 && (
        <div className="meta" style={{ marginBottom: 12 }}>
          Also counted this period, and replaced by the count above:{' '}
          {sheet.otherSessions.map((o) => `${formatLongDate(o.businessDate)} (${o.source === 'app' ? o.submittedBy : 'spreadsheet'})`).join(', ')}.
        </div>
      )}

      {sheet.items.every((i) => !i.onList) && (
        <div className="banner banner--info">
          <div>
            No count list is set up for {sheet.location.type} yet — an Operations Admin sets it under Supplies.
            {!readOnly && ' You can still add the supplies you counted below.'}
          </div>
        </div>
      )}
      <div className="card">
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
            <div>
              <label className="field-label" htmlFor="count-date">DAY COUNTED</label>
              <input
                id="count-date"
                type="date"
                className="control"
                value={businessDate}
                min={sheet.periodStart}
                max={lastDay}
                disabled={readOnly || !weekly}
                onChange={(e) => setBusinessDate(e.target.value)}
              />
            </div>
            <div className="meta" style={{ maxWidth: 420 }}>
              {weekly
                ? `Counts are due end of day Wednesday and count as on time through ${formatLongDate(sheet.closesOn)}.`
                : `Counted daily; on time through ${formatLongDate(sheet.closesOn)}.`}
            </div>
          </div>

          <div className="table-wrap" style={{ marginBottom: 14 }}>
            <table className="table">
              <thead>
                <tr>
                  <SortableHeader label="SUPPLY" column="supply" sort={sort} onSort={onSort} />
                  <th className="num">ON HAND</th>
                  <SortableHeader label="MINIMUM" column="minimum" sort={sort} onSort={onSort} />
                  {!readOnly && <th aria-label="Remove" />}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={readOnly ? 3 : 4} className="meta">Nothing to count yet.</td></tr>
                )}
                {shownRows.map((item) => {
                  const k = keyOf(item.equipmentTypeId);
                  const short = filled(k) && item.target != null && Number(values[k]) < item.target.minimum;
                  return (
                    <tr key={item.equipmentTypeId}>
                      <td>
                        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{item.name}</span>
                        {!item.onList && (
                          <span className="meta" style={{ marginLeft: 8 }} title={`Not on the ${sheet.location.type} count list`}>
                            added
                          </span>
                        )}
                      </td>
                      <td className="num">
                        <input
                          id={`count-${item.equipmentTypeId}`}
                          className="control"
                          inputMode="numeric"
                          autoComplete="off"
                          aria-label={item.name}
                          style={{
                            width: 100, textAlign: 'right',
                            borderColor: !filled(k) && !readOnly ? 'var(--orange-border)' : undefined,
                          }}
                          value={values[k] ?? ''}
                          disabled={readOnly}
                          onChange={(e) => setValue(k, e.target.value)}
                        />
                      </td>
                      <td className="meta">
                        {item.target ? (
                          <span style={short ? { color: 'var(--red)', fontWeight: 700 } : undefined}>
                            {item.target.minimum.toLocaleString()} · {TARGET_SOURCE[item.target.source]}
                            {short && ' · below'}
                          </span>
                        ) : 'No target'}
                      </td>
                      {!readOnly && (
                        <td style={{ width: 1, whiteSpace: 'nowrap' }}>
                          {!item.onList && (
                            <button
                              className="btn btn--sm"
                              aria-label={`Take ${item.name} off this count`}
                              title="Take off this count"
                              onClick={() => removeSupply(item.equipmentTypeId)}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!readOnly && addable.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
              <select
                id="count-add-supply"
                className="control"
                aria-label="Add a supply"
                value=""
                onChange={(e) => { if (e.target.value) addSupply(Number(e.target.value)); }}
              >
                <option value="">+ Add a supply not listed…</option>
                {addable.map((e) => (
                  <option key={e.equipmentTypeId} value={e.equipmentTypeId}>{e.name}</option>
                ))}
              </select>
              <span className="meta">
                For supplies this location has that aren't on its list. An added supply stays on its sheet for the next count.
              </span>
            </div>
          )}

          <label className="field-label" htmlFor="count-note">NOTE</label>
          <textarea
            id="count-note"
            className="control"
            rows={2}
            style={{ width: '100%', maxWidth: 640 }}
            placeholder="Anything that explains an unusual number"
            value={note}
            disabled={readOnly}
            onChange={(e) => setNote(e.target.value)}
          />

          {!readOnly && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
              <button
                className="btn btn--primary"
                disabled={blanks > 0 || keys.length === 0 || save.isPending}
                onClick={() => { setError(null); save.mutate(); }}
              >
                {save.isPending ? 'Saving…' : sheet.appRowVersionNo > 0 ? 'Save correction' : 'Save count'}
              </button>
              <span className="meta">
                {blanks > 0
                  ? `${blanks} ${blanks === 1 ? 'box is' : 'boxes are'} still blank — enter 0 where there are none on hand.`
                  : 'Every item has a number.'}
              </span>
            </div>
          )}
        </div>
    </div>
  );
}
